/**
 * Layout, kolizje i proceduralne tekstury Lodziarni (świat 1600×720).
 * Gałki smaków = kolorowe kulki Graphics → generateTexture (bez emoji).
 */
import Phaser from 'phaser';
import type { ColliderRect } from '../../core/world/index.js';
import {
  ICE_CREAM_CONE_TEXTURE,
  ICE_CREAM_FLAVORS,
  heldIceCreamTextureKey,
  type IceCreamFlavorId,
  type IceCreamVessel,
} from './icecreamLogic.js';

export const ICECREAM_WORLD_W = 1600;
export const ICECREAM_WORLD_H = 720;

const TEX_SIZE = 128;

export type IcecreamDecorKind =
  | IceCreamFlavorId
  | 'cone'
  | 'showcase';

/** Mapa texture key → rodzaj rysunku. */
export const ICECREAM_TEXTURE_SHAPES: Readonly<
  Record<string, IcecreamDecorKind>
> = {
  icecream_flavor_strawberry: 'strawberry',
  icecream_flavor_chocolate: 'chocolate',
  icecream_flavor_vanilla: 'vanilla',
  icecream_flavor_mint: 'mint',
  [ICE_CREAM_CONE_TEXTURE]: 'cone',
  icecream_showcase: 'showcase',
};

/** Lada — kolizja (piesek stoi przed ladą). */
export const COUNTER = {
  x: 800,
  y: 300,
  w: 720,
  h: 70,
} as const;

/** Punkt podejścia do lady (zakup). */
export const COUNTER_APPROACH = { x: 800, y: 420 } as const;

export const ICECREAM_COLLIDERS: ColliderRect[] = [
  // lada
  {
    x: COUNTER.x - COUNTER.w / 2,
    y: COUNTER.y - COUNTER.h / 2,
    width: COUNTER.w,
    height: COUNTER.h,
  },
  // witryna z tyłu lady
  { x: 440, y: 160, width: 720, height: 40 },
  // stoliki (elipsy → AABB) — nie nachodzą na PLAYER_START
  { x: 220, y: 500, width: 120, height: 50 },
  { x: 1180, y: 520, width: 120, height: 50 },
  { x: 500, y: 560, width: 100, height: 44 },
];

/** Czy punkt jest wolny od colliderów (testy + sanity spawnu). */
export function isIcecreamPointClear(
  x: number,
  y: number,
  radius = 22,
  colliders: readonly ColliderRect[] = ICECREAM_COLLIDERS,
): boolean {
  for (const c of colliders) {
    const nearestX = Math.max(c.x, Math.min(x, c.x + c.width));
    const nearestY = Math.max(c.y, Math.min(y, c.y + c.height));
    const dx = x - nearestX;
    const dy = y - nearestY;
    if (dx * dx + dy * dy < radius * radius) return false;
  }
  return true;
}

/**
 * Start gracza — poza colliders lady i stolików (r≥22).
 * Stary (200,480) ocierał się o stolik {220,500,120×50}.
 */
export const PLAYER_START = { x: 160, y: 430 } as const;

/** Stoliki — klik = siadanie (pozycja wizualna + podejście). */
export const ICECREAM_TABLES = [
  { id: 'table-a', x: 280, y: 520, approachX: 280, approachY: 470 },
  { id: 'table-b', x: 1240, y: 540, approachX: 1240, approachY: 490 },
  { id: 'table-c', x: 550, y: 580, approachX: 550, approachY: 530 },
] as const;

/** Sprzedawczyni za ladą — klikalne powitanie. */
export const VENDOR_POS = { x: 980, y: 250 } as const;

/**
 * Upewnij się, że wszystkie tekstury lodziarni istnieją.
 * PNG z Preload mają pierwszeństwo; brak → narysuj proceduralnie.
 */
export function ensureIcecreamTextures(scene: Phaser.Scene): void {
  for (const f of ICE_CREAM_FLAVORS) {
    ensureIcecreamTexture(scene, f.textureKey);
  }
  ensureIcecreamTexture(scene, ICE_CREAM_CONE_TEXTURE);
  ensureIcecreamTexture(scene, 'icecream_showcase');
}

export function ensureIcecreamTexture(
  scene: Phaser.Scene,
  textureKey: string,
): string {
  if (scene.textures.exists(textureKey)) return textureKey;
  const kind = ICECREAM_TEXTURE_SHAPES[textureKey];
  if (!kind) return textureKey;

  const g = scene.make.graphics({ x: 0, y: 0 });
  drawIcecreamShape(g, kind, TEX_SIZE, TEX_SIZE);
  g.generateTexture(textureKey, TEX_SIZE, TEX_SIZE);
  g.destroy();
  return textureKey;
}

/**
 * Jedna tekstura całego zamówienia, używana przez mouth-anchor awatara.
 * Kolejne warianty są cache'owane w TextureManagerze i podmieniane po ugryzieniu.
 */
export function ensureHeldIceCreamTexture(
  scene: Phaser.Scene,
  vessel: IceCreamVessel,
  scoops: readonly IceCreamFlavorId[],
): string {
  const key = heldIceCreamTextureKey(vessel, scoops);
  if (scene.textures.exists(key)) return key;

  const width = 96;
  const height = 128;
  const cx = width / 2;
  const g = scene.make.graphics({ x: 0, y: 0 });

  if (vessel === 'cup') {
    g.fillStyle(0xfff8f0, 1);
    g.fillRoundedRect(cx - 20, 70, 40, 42, 9);
    g.lineStyle(3, 0xe0d4c8, 1);
    g.strokeRoundedRect(cx - 20, 70, 40, 42, 9);
    g.fillStyle(0xffb7c5, 0.8);
    g.fillRoundedRect(cx - 22, 67, 44, 9, 4);
  } else {
    g.fillStyle(0xd4a574, 1);
    g.fillTriangle(cx - 20, 68, cx + 20, 68, cx, 118);
    g.lineStyle(2, 0xb07d4f, 0.8);
    g.lineBetween(cx - 13, 74, cx + 7, 103);
    g.lineBetween(cx + 12, 74, cx - 5, 105);
    g.lineBetween(cx - 15, 82, cx + 10, 92);
  }

  scoops.slice(0, 3).forEach((id, index) => {
    const flavor = ICE_CREAM_FLAVORS.find((entry) => entry.id === id);
    const y = 64 - index * 20;
    g.fillStyle(0x000000, 0.1);
    g.fillEllipse(cx + 2, y + 10, 38, 12);
    g.fillStyle(flavor?.color ?? 0xff8fab, 1);
    g.fillCircle(cx, y, 19);
    g.fillStyle(0xffffff, 0.38);
    g.fillCircle(cx - 6, y - 7, 6);
    g.lineStyle(2, 0xffffff, 0.72);
    g.strokeCircle(cx, y, 19);
  });

  g.generateTexture(key, width, height);
  g.destroy();
  return key;
}

export function icecreamShapeForTexture(
  textureKey: string,
): IcecreamDecorKind | null {
  return ICECREAM_TEXTURE_SHAPES[textureKey] ?? null;
}

export function drawIcecreamShape(
  g: Phaser.GameObjects.Graphics,
  kind: IcecreamDecorKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;
  switch (kind) {
    case 'strawberry':
      drawScoop(g, cx, cy, 0xff8fab, 0xffc1d6);
      break;
    case 'chocolate':
      drawScoop(g, cx, cy, 0x8d6e63, 0xbcaaa4);
      break;
    case 'vanilla':
      drawScoop(g, cx, cy, 0xfff3e0, 0xffe0b2);
      break;
    case 'mint':
      drawScoop(g, cx, cy, 0x81c784, 0xa5d6a7);
      break;
    case 'cone':
      drawCone(g, cx, cy);
      break;
    case 'showcase':
      drawShowcase(g, cx, cy);
      break;
  }
}

function drawScoop(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  main: number,
  highlight: number,
): void {
  // cień
  g.fillStyle(0x000000, 0.12);
  g.fillEllipse(cx + 4, cy + 28, 70, 22);
  // gałka
  g.fillStyle(main, 1);
  g.fillCircle(cx, cy - 4, 42);
  g.fillStyle(highlight, 0.85);
  g.fillCircle(cx - 12, cy - 18, 16);
  g.fillStyle(0xffffff, 0.35);
  g.fillCircle(cx - 14, cy - 20, 8);
  // małe kropki smaku
  g.fillStyle(highlight, 0.7);
  g.fillCircle(cx + 14, cy + 6, 5);
  g.fillCircle(cx - 6, cy + 14, 4);
  g.lineStyle(3, 0xffffff, 0.75);
  g.strokeCircle(cx, cy - 4, 42);
}

function drawCone(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // wafelek
  g.fillStyle(0xd4a574, 1);
  g.fillTriangle(cx - 28, cy - 10, cx + 28, cy - 10, cx, cy + 52);
  g.fillStyle(0xc4956a, 1);
  g.fillTriangle(cx - 18, cy - 6, cx + 18, cy - 6, cx, cy + 40);
  // kratka
  g.lineStyle(2, 0xb07d4f, 0.85);
  g.lineBetween(cx - 16, cy, cx - 4, cy + 28);
  g.lineBetween(cx + 4, cy, cx + 14, cy + 26);
  g.lineBetween(cx - 20, cy + 8, cx + 10, cy + 20);
  // gałka waniliowa na wierzchu
  g.fillStyle(0xfff3e0, 1);
  g.fillCircle(cx, cy - 22, 26);
  g.fillStyle(0xffffff, 0.45);
  g.fillCircle(cx - 8, cy - 30, 10);
}

function drawShowcase(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // rama witryny
  g.fillStyle(0xb3e5fc, 0.95);
  g.fillRoundedRect(cx - 56, cy - 40, 112, 80, 10);
  g.fillStyle(0xe3f2fd, 0.9);
  g.fillRoundedRect(cx - 48, cy - 32, 96, 64, 8);
  g.lineStyle(4, 0xffffff, 0.95);
  g.strokeRoundedRect(cx - 56, cy - 40, 112, 80, 10);
  // odblask
  g.fillStyle(0xffffff, 0.35);
  g.fillRect(cx - 44, cy - 28, 18, 52);
}
